import { GoogleOAuthProvider } from "@react-oauth/google";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";

import { AppConfig } from "~/AppConfig";
import type {
  Error as ApiError,
  SubscriptionLinkRegistration,
} from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import { queryKeys } from "~/api/queryKeys";
import FormLabel from "~/components/login/formLabel/FormLabel";
import FormTitle from "~/components/login/formTitle/FormTitle";
import PasswordInput from "~/components/login/passwordInput/PasswordInput";
import TermsOfServiceNotice from "~/components/login/termsOfServiceNotice/TermsOfServiceNotice";
import NotificationBlock from "~/components/notificationBlock/NotificationBlock";
import Button from "~/components/ui/button/Button";
import FormBaseErrors from "~/components/ui/formBaseErrors/FormBaseErrors";
import Input from "~/components/ui/input/Input";
import Spinner from "~/components/ui/spinner/Spinner";
import { PAGE_TITLES } from "~/configs/pageTitles";
import { EXPECTS_FORBIDDEN_META } from "~/configs/queryClient";
import { useAuth } from "~/contexts/AuthProvider";
import { EMAIL_VALIDATION_REG_EX } from "~/helpers/helpers";
import labels from "~/helpers/labels";
import { tryCatch } from "~/helpers/tryCatch";
import { useFormErrors } from "~/hooks/useFormErrors";
import { usePageTitle } from "~/hooks/usePageTitle";
import { routes } from "~/router";
import { queueReditusConversion } from "~/tracking/reditus";
import { VALIDATION_ERRORS } from "~/utils/errors/validationErrors";
import { isWhiteLabel } from "~/utils/white-label";

import styles from "./Login.module.scss";
import GoogleLoginButton from "./googleLoginButton/GoogleLoginButton";

type ActivateAccountFormValues = {
  first_name: string;
  last_name: string;
  password: string;
  // Sent only after the link's email is rejected.
  email: string;
};

const ActivateAccountPage = () => {
  usePageTitle(PAGE_TITLES.activateAccount);

  const { code } = useParams<"code">();
  const navigate = useNavigate();
  const { token, user, isLoading: isAuthLoading, setSession } = useAuth();
  const [isLinkInvalid, setIsLinkInvalid] = useState(false);
  const [isLinkExpired, setIsLinkExpired] = useState(false);
  // A 5xx or lost response may still have created the account.
  const [unexpectedFailureVia, setUnexpectedFailureVia] = useState<
    "password" | "google" | null
  >(null);
  const [linkEmailWarning, setLinkEmailWarning] = useState<string | null>(null);
  const isEmailRequested = linkEmailWarning !== null;
  const [activatedToken, setActivatedToken] = useState<string | null>(null);
  // The link was submitted from this page: its answer is the one to tell.
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const isActivated = activatedToken !== null;
  // Stays on after a successful Google sign-up, until the redirect.
  const [isGoogleSubmitting, setIsGoogleSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    getValues,
    setError,
    setFocus,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<ActivateAccountFormValues>();
  const isBusy = isSubmitting || isGoogleSubmitting || isActivated;
  const { baseErrors, applyServerErrors, clearBaseErrors } =
    useFormErrors(setError);

  const { mutateAsync } = useMutation({
    mutationFn: (data: SubscriptionLinkRegistration) =>
      apiClient.billing.subscriptionLinksBootstrapCreate(code!, data),
  });

  // The link, checked on open: an expired one (410), a used one (403) or an
  // unknown one (404) is told before the form. Any other answer leaves it to the
  // submit. Not after this page's sign-up: the link is used by then.
  const { data: linkStatus } = useQuery({
    queryKey: queryKeys.subscriptionLinkStatus(code),
    enabled: !!code && !isAuthLoading && !isActivated,
    // Asked again on every visit: a link gets used.
    gcTime: 0,
    // The answer never throws, so no error reaches retryOnNetworkError (a
    // 401 there signs the user out) or SubscriptionSync; these keep it so
    // should that change.
    retry: false,
    meta: EXPECTS_FORBIDDEN_META,
    queryFn: async (): Promise<"valid" | "invalid" | "expired" | "unknown"> => {
      const { error } = await tryCatch(
        apiClient.billing.subscriptionLinksDetail(code!),
      );

      if (!error) return "valid";
      if (error.status === 410) return "expired";

      return error.status === 403 || error.status === 404
        ? "invalid"
        : "unknown";
    },
  });
  // Before the form, or after a submit if a sign-in in another tab cleared
  // the cache (the link rechecked, maybe used by that very submit); off once
  // the account is created.
  const isLinkRejected = linkStatus === "invalid" || linkStatus === "expired";
  const isOfferExpired = linkStatus === "expired" || isLinkExpired;
  // Someone signed in clicked a link from the email again: nothing to do
  // here. Not once they've submitted: the page tells what came of it (the
  // sign-up, or its error with a sign-in hint). An expired offer is told
  // to them too.
  const isLeavingForHome =
    linkStatus === "invalid" && !!token && !hasSubmitted && !isBusy;

  // The form would flash before the redirect or the "Invalid Link" or
  // "Offer Expired" screen.
  const isLinkChecking =
    !!code && !isActivated && (isAuthLoading || linkStatus === undefined);
  // Mid-submit, a rejected link waits for the submit's answer.
  const isSpinnerShown =
    isLinkChecking || isLeavingForHome || (isLinkRejected && isBusy);

  useEffect(() => {
    if (isLeavingForHome) navigate(routes.home(), { replace: true });
  }, [isLeavingForHome, navigate]);

  // Once the form is back: the field may have come in behind the spinner.
  useEffect(() => {
    if (isEmailRequested && !isSpinnerShown) setFocus("email");
  }, [isEmailRequested, isSpinnerShown, setFocus]);

  useEffect(() => {
    // Wait for the new user: the sign_up event needs its email.
    if (!activatedToken || isAuthLoading) return;

    // A 401 or a sign-in in another tab may have replaced the session.
    const isNewAccountSignedIn = token === activatedToken && user !== null;

    // Same GTM event as SignUpPage.
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "sign_up",
      ...(isNewAccountSignedIn &&
        user.email && { email: user.email.trim().toLowerCase() }),
    });

    // The queue is per browser: MainView flushes it for whoever is signed in.
    if (isNewAccountSignedIn) queueReditusConversion();

    navigate(routes.home(), { replace: true });
  }, [activatedToken, token, isAuthLoading, user, navigate]);

  if (isSpinnerShown) {
    return (
      <div role="status" aria-label="Checking the link">
        <Spinner />
      </div>
    );
  }

  if (isOfferExpired) {
    return (
      <>
        <FormTitle title="Offer Expired" />
        <div className={styles.infoText}>Sorry, this offer has expired.</div>
        <div className={styles.footer}>
          <Link className={styles.footerLink} to={routes.auth.login()}>
            {labels.BACK_TO_LOGIN}
          </Link>
        </div>
      </>
    );
  }

  if (!code || isLinkInvalid || isLinkRejected) {
    const accountEmail = isEmailRequested
      ? getValues("email")
      : "the email this link was sent to";
    const signInHint =
      unexpectedFailureVia === "google"
        ? "log in with Google"
        : `sign in with ${accountEmail} and the password you just set`;

    return (
      <>
        <FormTitle title="Invalid Link" />
        <div className={styles.infoText}>
          {unexpectedFailureVia
            ? `This link has already been used. If your previous attempt went through, your account is ready — ${signInHint}.`
            : "This link is invalid or has already been used."}
        </div>
        <div className={styles.footer}>
          <Link className={styles.footerLink} to={routes.auth.login()}>
            {labels.BACK_TO_LOGIN}
          </Link>
        </div>
      </>
    );
  }

  // Returns whether the account was created.
  const submitBootstrap = async (
    body: SubscriptionLinkRegistration,
  ): Promise<boolean> => {
    const isGoogle = body.google_code !== undefined;

    clearBaseErrors();
    setHasSubmitted(true);

    // Plain `tryCatch`: the handling branches on the HTTP status.
    const result = await tryCatch(mutateAsync(body));

    if (result.error) {
      const response = result.error;

      if (response instanceof Response && response.status === 404) {
        setIsLinkInvalid(true);

        return false;
      }

      // Expired after the page opened.
      if (response instanceof Response && response.status === 410) {
        setIsLinkExpired(true);

        return false;
      }

      // Google rejected the code; nothing was created.
      if (isGoogle && response instanceof Response && response.status === 401) {
        setUnexpectedFailureVia(null);
        applyServerErrors({
          code: 0,
          message: ERROR_MESSAGES.GOOGLE_SIGN_IN_FAILED,
        });

        return false;
      }

      const apiError =
        response instanceof Response && response.status === 422
          ? await parseApiError(response)
          : null;

      if (!apiError) {
        setUnexpectedFailureVia(isGoogle ? "google" : "password");
        applyServerErrors({
          code: 0,
          message: ERROR_MESSAGES.UNEXPECTED_FAILURE,
        });

        return false;
      }

      // A 422 means the link is still unredeemed.
      setUnexpectedFailureVia(null);

      const { email: emailErrors, ...otherValidations } =
        apiError.validations ?? {};

      // Google's email is used as is: there is no email input to fall back to.
      if (isGoogle) {
        const isTaken = apiError.validation_codes?.includes(
          VALIDATION_ERRORS.USER_EMAIL_TAKEN,
        );
        const hasNameErrors =
          !!otherValidations.first_name || !!otherValidations.last_name;
        // Only a name we didn't send can be one Google didn't share.
        const isNameMissing =
          (!!otherValidations.first_name && !body.first_name) ||
          (!!otherValidations.last_name && !body.last_name);

        applyServerErrors(
          keepFieldValidations(
            apiError.validations
              ? {
                  ...apiError,
                  validations: {
                    ...(isTaken ? otherValidations : apiError.validations),
                    base: [
                      ...(otherValidations.base ?? []),
                      // The link only creates new accounts: logging in
                      // wouldn't redeem it.
                      ...(isTaken ? [ERROR_MESSAGES.GOOGLE_ACCOUNT_TAKEN] : []),
                      // The Google code is single-use: the user must click again.
                      ...(hasNameErrors
                        ? [
                            isNameMissing
                              ? ERROR_MESSAGES.GOOGLE_NAME_MISSING
                              : ERROR_MESSAGES.GOOGLE_NAME_INVALID,
                          ]
                        : []),
                    ],
                  },
                }
              : apiError,
            NAME_FIELDS,
          ),
        );

        return false;
      }

      // The link's email was rejected: ask for another one.
      if (emailErrors && !isEmailRequested) {
        const isTaken = apiError.validation_codes?.includes(
          VALIDATION_ERRORS.USER_EMAIL_TAKEN,
        );

        setLinkEmailWarning(
          isTaken
            ? ERROR_MESSAGES.LINK_EMAIL_TAKEN
            : ERROR_MESSAGES.linkEmailRejected(emailErrors),
        );
        applyServerErrors(
          keepFieldValidations(
            { ...apiError, validations: otherValidations },
            NAME_PASSWORD_FIELDS,
          ),
        );

        return false;
      }

      applyServerErrors(
        keepFieldValidations(
          apiError,
          isEmailRequested
            ? [...NAME_PASSWORD_FIELDS, "email"]
            : NAME_PASSWORD_FIELDS,
        ),
      );

      return false;
    }

    setSession(result.data.data.access_token);
    setActivatedToken(result.data.data.access_token.key);

    return true;
  };

  const onSubmit = async ({ email, ...data }: ActivateAccountFormValues) => {
    await submitBootstrap({ ...data, ...(isEmailRequested && { email }) });
  };

  // Google provides the email; typed names take precedence over Google's.
  const onGoogleCode = async (googleCode: string) => {
    // The popup doesn't block the form: a password submit or a second Google
    // code may already be in flight, and a parallel request would 404.
    if (isBusy) return;

    const firstName = getValues("first_name")?.trim();
    const lastName = getValues("last_name")?.trim();
    let isCreated = false;

    clearErrors();
    setIsGoogleSubmitting(true);

    try {
      isCreated = await submitBootstrap({
        google_code: googleCode,
        ...(firstName && { first_name: firstName }),
        ...(lastName && { last_name: lastName }),
      });
    } finally {
      if (!isCreated) setIsGoogleSubmitting(false);
    }
  };

  return (
    <>
      <FormTitle title="Your platform is ready" />
      <div className={styles.infoText}>
        Tell us how we should address you and choose a password to open your
        platform.
      </div>
      {token && !isActivated && (
        <div className={styles.notificationWrapper}>
          <NotificationBlock type="warning">
            {user?.email
              ? `You are currently signed in as ${user.email}. Continuing will sign you out of that account.`
              : "You are currently signed in. Continuing will sign you out of that account."}
          </NotificationBlock>
        </div>
      )}
      <FormBaseErrors errors={baseErrors} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.inputsFlexWrapper}>
          <div className={styles.inputFlexWrapper}>
            <FormLabel label={labels.FIRST_NAME} />
            <Input
              autoComplete="given-name"
              errormessage={errors.first_name?.message ?? ""}
              {...register("first_name", {
                required: labels.THIS_FIELD_CANNOT_BE_EMPTY,
                // Google-path errors aren't revalidated by RHF.
                onChange: () => clearErrors("first_name"),
              })}
              disabled={isBusy}
            />
          </div>
          <div className={styles.inputFlexWrapper}>
            <FormLabel label={labels.LAST_NAME} />
            <Input
              autoComplete="family-name"
              errormessage={errors.last_name?.message ?? ""}
              {...register("last_name", {
                required: labels.THIS_FIELD_CANNOT_BE_EMPTY,
                // Google-path errors aren't revalidated by RHF.
                onChange: () => clearErrors("last_name"),
              })}
              disabled={isBusy}
            />
          </div>
        </div>
        {isEmailRequested && (
          <div className={styles.inputsWrapper}>
            <div className={styles.notificationWrapper}>
              <NotificationBlock type="warning">
                {linkEmailWarning}
              </NotificationBlock>
            </div>
            <FormLabel label={labels.EMAIL} />
            <Input
              autoComplete="email"
              errormessage={errors.email?.message ?? ""}
              {...register("email", {
                required: labels.THIS_FIELD_CANNOT_BE_EMPTY,
                pattern: {
                  value: EMAIL_VALIDATION_REG_EX,
                  message: labels.INVALID_EMAIL,
                },
              })}
              disabled={isBusy}
            />
          </div>
        )}
        <div className={styles.inputsWrapper}>
          <PasswordInput
            passwordError={errors.password?.message}
            // @ts-expect-error refactor password to T type
            register={register}
            field="password"
            // Otherwise Chrome fills a saved login into Last name + Password.
            autoComplete="new-password"
            // @ts-expect-error refactor password to T type
            getValues={getValues}
            disabled={isBusy}
          />
        </div>
        <TermsOfServiceNotice />
        <Button
          fullWidth
          type="submit"
          disabled={isBusy}
          withSpinner={isBusy && !isGoogleSubmitting}
        >
          Create account
        </Button>
      </form>
      {!isWhiteLabel() && (
        <div className={styles.buttonsWrapper}>
          <div className={styles.dividerWrapper}>
            <div className={styles.divider} />
            <p className={styles.orText}>{labels.OR}</p>
            <div className={styles.divider} />
          </div>
          <GoogleOAuthProvider clientId={AppConfig.GOOGLE_AUTH_CLIENT_ID}>
            <GoogleLoginButton
              buttonText="Sign up with Google"
              isLoading={isGoogleSubmitting}
              disabled={isBusy}
              onCode={onGoogleCode}
            />
          </GoogleOAuthProvider>
        </div>
      )}
    </>
  );
};

const ERROR_MESSAGES = {
  UNEXPECTED_FAILURE: "Something went wrong. Please try again.",
  GOOGLE_SIGN_IN_FAILED:
    "Google sign-in failed. Try again, or create the account with a password above.",
  GOOGLE_ACCOUNT_TAKEN:
    "This Google account is already registered. Use a different Google account, or create the account with a password above.",
  GOOGLE_NAME_MISSING:
    "Google didn't share your name. Enter it and click Sign up with Google again.",
  GOOGLE_NAME_INVALID: "Fix your name and click Sign up with Google again.",
  LINK_EMAIL_TAKEN:
    "An account with the email this link was sent to already exists. Enter a different email to continue.",
  linkEmailRejected: (errors: string[]) =>
    `We couldn't use the email on this link (${errors.join(", ")}). Enter a different email to continue.`,
};

const NAME_FIELDS: (keyof ActivateAccountFormValues)[] = [
  "first_name",
  "last_name",
];

const NAME_PASSWORD_FIELDS: (keyof ActivateAccountFormValues)[] = [
  ...NAME_FIELDS,
  "password",
];

// Errors on fields the form doesn't render (team, subscription) go to the
// base block: RHF would keep them forever and block every later submit.
function keepFieldValidations(
  apiError: ApiError,
  fields: (keyof ActivateAccountFormValues)[],
): ApiError {
  if (!apiError.validations) return apiError;

  const fieldNames: string[] = fields;
  const validations: Record<string, string[]> = { base: [] };

  for (const [field, messages] of Object.entries(apiError.validations)) {
    if (fieldNames.includes(field)) {
      validations[field] = messages;
    } else if (field === "base") {
      validations.base.push(...messages);
    } else {
      validations.base.push(`${humanizeField(field)} ${messages.join(", ")}`);
    }
  }

  return { ...apiError, validations };
}

// `current_plan` → "Current plan", like Rails' full messages.
function humanizeField(field: string) {
  const words = field.replace(/_/g, " ");

  return words.charAt(0).toUpperCase() + words.slice(1);
}

// A proxy error page is not JSON.
async function parseApiError(response: Response): Promise<ApiError | null> {
  const { data } = await tryCatch(response.json());

  return (data as { error?: ApiError } | null)?.error ?? null;
}

export default ActivateAccountPage;
